/* ==========================================================================
   Young Grandpa - top-level nav "peel" hover animation
   --------------------------------------------------------------------------
   Splits each top-level nav link's text (Shop / New Arrivals / Collections /
   About us) into one wrapped span per character, each holding two stacked
   copies of that character - assets/yg-custom.css slides the top copy up
   and out on hover while the bottom copy slides up into its place, staggered
   per character via a --yg-char-index custom property this file sets. All
   the actual motion is CSS; this file only ever builds and rebuilds the
   markup.

   Also targets footer link columns (.yg-footer-section .menu__item a) -
   shared with the primary nav rather than reimplemented. Submenu items
   (.mega-menu__link) and the mobile drawer (.menu-drawer__menu-item--mainlist)
   still don't match either selector, so neither is touched.

   Accessibility: the brief asked for aria-hidden on just the duplicate
   (bottom) copy, but splitting text into individual single-character spans
   has its own screen-reader cost even for the "visible" top copies - most
   screen readers either spell a run of one-letter spans out character by
   character or announce each as a separate boundary, neither of which reads
   as the actual word. Marking the whole per-character structure
   aria-hidden="true" and adding one plain, un-split .visually-hidden span
   with the real word (base.css's own utility class, not a new one) avoids
   that entirely and is still a correct superset of "hide the duplicate" -
   the link's accessible name comes from the hidden span; accessibility
   never sees the animated markup at all.

   The split has to be re-applied, and specifically OUTSIDE the theme editor.
   sections/header.liquid ends with `{% unless request.design_mode %}` around
   a hydrate() call, so on every ordinary page load - and never in the editor
   - the header re-fetches itself through the Section Rendering API about 4s
   in and morphs the server HTML back over the live DOM, replacing each
   title's contents with plain text. Measured on the preview: 58 characters
   split at DOMContentLoaded, 0 immediately after the morph. The footer is a
   different section, so its split survives, which is why the animation
   looked broken on the nav alone.

   data-skip-node-update (already on the nav <a>) does not help: morph.js
   only skips that node's own attribute update and still descends into its
   children. morphSection dispatches no event either, so the two hooks below
   are what re-apply the split - a one-shot watch on the section's
   data-hydrated flag, which section-hydration.js sets straight after the
   morph, and a guarded childList observer on .header-menu for any later
   re-render. split() is idempotent, so both are safe to fire repeatedly. */

(function () {
  'use strict';

  var SELECTOR = '.header-menu .menu-list__link-title, .yg-footer-section .menu__item a';
  var CHARS = '.yg-nav-chars';
  var MENU = '.header-menu';
  var SECTION = '.header-section';
  var SPACE = String.fromCharCode(32);
  var NBSP = String.fromCharCode(160);

  function init() {
    var titles = document.querySelectorAll(SELECTOR);

    for (var i = 0; i < titles.length; i++) {
      split(titles[i]);
    }
  }

  /**
   * @param {Element} title
   */
  function split(title) {
    if (title.querySelector(CHARS)) return;

    /* textContent returns the raw source text, including the Liquid
       template's indentation/newlines around {{ link.title }} - unlike
       normal rendering, that whitespace isn't browser-collapsed once it's
       re-wrapped character-by-character in white-space:pre spans below, so
       it must be collapsed here the way the browser would have. */
    var text = (title.textContent || '').trim().replace(/\s+/g, ' ');
    if (!text) return;

    var characters = document.createElement('span');
    characters.className = 'yg-nav-chars';
    characters.setAttribute('aria-hidden', 'true');

    for (var i = 0; i < text.length; i++) {
      var char = text[i];
      var isSpace = char === SPACE;
      /* Non-breaking space, not a plain one: a lone space character in its
         own span is at the mercy of whatever white-space handling applies
         and can end up collapsed to zero width in some contexts - NBSP
         renders as a real, non-collapsing space regardless. */
      var display = isSpace ? NBSP : char;

      var wrap = document.createElement('span');
      wrap.className = 'yg-nav-char' + (isSpace ? ' yg-nav-char--space' : '');
      wrap.style.setProperty('--yg-char-index', String(i));

      var top = document.createElement('span');
      top.className = 'yg-nav-char__face yg-nav-char__face--top';
      top.textContent = display;

      var bottom = document.createElement('span');
      bottom.className = 'yg-nav-char__face yg-nav-char__face--bottom';
      bottom.textContent = display;

      wrap.appendChild(top);
      wrap.appendChild(bottom);
      characters.appendChild(wrap);
    }

    var label = document.createElement('span');
    label.className = 'visually-hidden';
    label.textContent = text;

    title.textContent = '';
    title.appendChild(characters);
    title.appendChild(label);
  }

  var menuObserver = null;

  /* nav - re-split after any header re-render */
  function observeMenu() {
    if (!window.MutationObserver) return;

    var menu = document.querySelector(MENU);
    if (!menu) return;

    if (!menuObserver) menuObserver = new MutationObserver(refresh);

    menuObserver.observe(menu, { childList: true, subtree: true });
  }

  /* Disconnected across the split so the observer never sees its own writes,
     then re-attached against whatever .header-menu is current - a morph
     normally keeps that node, but a wholesale replacement would leave the
     old registration pointing at a detached element. */
  function refresh() {
    if (menuObserver) menuObserver.disconnect();

    init();
    observeMenu();
  }

  /* nav - one-shot watch on the header hydration morph */
  function watchHydration() {
    if (!window.MutationObserver) return;

    var section = document.querySelector(SECTION);
    if (!section) return;

    if (section.getAttribute('data-hydrated') === 'true') {
      refresh();
      return;
    }

    var observer = new MutationObserver(function () {
      if (section.getAttribute('data-hydrated') !== 'true') return;

      observer.disconnect();
      refresh();
    });

    observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
  }

  function start() {
    refresh();
    watchHydration();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
